import { useCallback, useEffect, useState } from 'react';
import type { Lead, LeadInterest } from '@shared/types';
import { site } from '@shared/site.config';
import { api } from '@/lib/api';
import { useTitle } from '@/lib/useTitle';

const TOKEN_KEY = 'pinnacle-admin-token';

const INTEREST_LABEL: Record<LeadInterest, string> = {
  'buying-selling': 'Buying or selling',
  leasing: 'Leasing',
  'business-listing': 'Business listing',
  general: 'General inquiry',
};

export default function Admin() {
  useTitle('Leads');
  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem(TOKEN_KEY));
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filter, setFilter] = useState<'all' | 'open'>('open');
  const [openId, setOpenId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (t: string) => {
    setLoading(true);
    try {
      const data = await api.adminLeads(t);
      setLeads(data.leads);
      setError('');
    } catch (err) {
      // A 401 means the server restarted and dropped in-memory sessions.
      sessionStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setError(err instanceof Error ? err.message : 'Session expired.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) void load(token);
  }, [token, load]);

  if (!token) {
    return (
      <Login
        onToken={(t) => {
          sessionStorage.setItem(TOKEN_KEY, t);
          setToken(t);
        }}
        notice={error}
      />
    );
  }

  const visible = filter === 'all' ? leads : leads.filter((l) => !l.contacted);

  async function toggleContacted(lead: Lead) {
    if (!token) return;
    const updated = await api.updateLead(token, lead.id, { contacted: !lead.contacted }).catch(() => null);
    if (updated) setLeads((prev) => prev.map((l) => (l.id === lead.id ? updated : l)));
  }

  return (
    <div className="mx-auto max-w-6xl px-5 sm:px-8 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="annot">{site.brand.name} · Brokerage</p>
          <h1 className="mt-2.5 font-display text-[36px] font-semibold leading-tight">Leads</h1>
        </div>
        <button
          onClick={() => {
            sessionStorage.removeItem(TOKEN_KEY);
            setToken(null);
          }}
          className="font-mono text-[10px] uppercase tracking-wider2 text-annot hover:text-graphite"
        >
          Sign out
        </button>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-2">
        {(['open', 'all'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`border px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-wider2 transition-colors ${
              filter === f
                ? 'border-blueprint bg-blueprint text-white'
                : 'border-medline text-annot hover:border-graphite hover:text-graphite'
            }`}
          >
            {f === 'open' ? 'Not yet contacted' : 'All'}
          </button>
        ))}
        {loading && <span className="ml-2 font-mono text-[10px] uppercase tracking-wider2 text-annot">Refreshing…</span>}
      </div>

      <div className="mt-6 divide-y divide-hairline border border-medline bg-sheet">
        {visible.length === 0 ? (
          <div className="p-16 text-center">
            <h2 className="font-display text-[24px]">No leads {filter === 'open' && 'waiting on a reply'}</h2>
            <p className="mt-2 text-[14px] text-annot">
              Inquiries from the contact form land here the moment they're submitted.
            </p>
          </div>
        ) : (
          visible.map((lead) => {
            const open = openId === lead.id;
            return (
              <div key={lead.id}>
                <button
                  onClick={() => setOpenId(open ? null : lead.id)}
                  className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 px-5 py-4 text-left transition-colors hover:bg-table/60"
                >
                  <span
                    className={`shrink-0 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider2 ${
                      lead.contacted ? 'border border-medline text-annot' : 'bg-redline text-white'
                    }`}
                  >
                    {lead.contacted ? 'contacted' : 'new'}
                  </span>
                  <span className="text-[15px] font-medium">{lead.name}</span>
                  {lead.company && <span className="text-[13px] text-annot">{lead.company}</span>}
                  <span className="text-[13px] text-annot">{INTEREST_LABEL[lead.interest]}</span>
                  {!lead.emailed && (
                    <span
                      className="font-mono text-[10px] uppercase tracking-wider2 text-redline"
                      title="Saved here, but the notification email did not send"
                    >
                      email not sent
                    </span>
                  )}
                  <span className="ml-auto shrink-0 font-mono text-[11px] tabular-nums text-annot">
                    {new Date(lead.createdAt).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </span>
                </button>

                {open && (
                  <div className="bg-table/50 px-5 pb-6 pt-1">
                    <div className="grid gap-6 md:grid-cols-[1fr_240px]">
                      <div>
                        <div className="flex flex-wrap gap-x-6 gap-y-1 text-[14px]">
                          <a href={`mailto:${lead.email}`} className="text-redline hover:underline">
                            {lead.email}
                          </a>
                          {lead.phone && (
                            <a href={`tel:${lead.phone.replace(/\D/g, '')}`} className="text-redline hover:underline">
                              {lead.phone}
                            </a>
                          )}
                        </div>
                        <p className="mt-4 whitespace-pre-wrap border border-hairline bg-sheet p-4 text-[15px] leading-relaxed text-graphite-600">
                          {lead.message}
                        </p>
                      </div>

                      <dl className="space-y-2.5 text-[13px]">
                        <div className="flex justify-between gap-3">
                          <dt className="text-annot">Interest</dt>
                          <dd>{INTEREST_LABEL[lead.interest]}</dd>
                        </div>
                        <div className="flex justify-between gap-3">
                          <dt className="text-annot">Page</dt>
                          <dd className="break-all text-right">{lead.sourcePath}</dd>
                        </div>
                        <div className="flex justify-between gap-3">
                          <dt className="text-annot">Emailed</dt>
                          <dd>{lead.emailed ? 'Yes' : 'No'}</dd>
                        </div>

                        <div className="border-t border-hairline pt-3">
                          <button
                            onClick={() => void toggleContacted(lead)}
                            className={lead.contacted ? 'btn-ghost w-full' : 'btn-primary w-full'}
                          >
                            {lead.contacted ? 'Mark as not contacted' : 'Mark as contacted'}
                          </button>
                        </div>
                      </dl>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function Login({ onToken, notice }: { onToken: (t: string) => void; notice?: string }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(notice ?? '');
  const [busy, setBusy] = useState(false);

  return (
    <div className="mx-auto max-w-sm px-5 py-28">
      <p className="annot">{site.brand.name}</p>
      <h1 className="mt-2.5 font-display text-[32px] font-semibold leading-tight">Brokerage sign-in</h1>
      <p className="mt-3 text-[14px] leading-relaxed text-annot">
        The lead dashboard. Set <code className="font-mono text-graphite">ADMIN_PASSWORD</code> in{' '}
        <code className="font-mono text-graphite">.env</code> to enable it.
      </p>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError('');
          try {
            const { token } = await api.adminLogin(password);
            onToken(token);
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Sign-in failed.');
          } finally {
            setBusy(false);
          }
        }}
        className="mt-7"
      >
        <label className="block">
          <span className="annot">Password</span>
          <input
            type="password"
            required
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="field mt-1.5"
            autoComplete="current-password"
          />
        </label>
        {error && (
          <div className="mt-3 border-l-2 border-redline bg-redline-100/50 px-4 py-3">
            <p className="text-[13px] leading-relaxed text-graphite-600">{error}</p>
          </div>
        )}
        <button type="submit" disabled={busy} className="btn-primary mt-4 w-full">
          {busy ? 'Checking…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
